import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Typography, Spin, Select } from "@douyinfe/semi-ui";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatAmountZhCn } from "../../utils";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();
const { Text } = Typography;

interface MonthRow {
  id: number;
  yearMonth: string;
  outMoneyAmount: number;
  outMoneyAmountReleased: number;
  outMoneyAmountNotReleased: number;
  creditHelpRepaymentPlanAmount: number;
  loanHelpRepaymentPlanAmount: number;
  cardHelpRepaymentPlanAmount: number;
  postingHelpAmount: number;
  borrowAmount: number;
  borrowReturnAmount: number;
  interestAmount: number;
  commissionAmount: number;
  planHelpAmount: number;
  planBorrowAmount: number;
  children?: MonthRow[];
  childRows?: MonthRow[];
  outMoneyUserId?: number;
  outMoneyUserName?: string;
  leadId?: number;
  leadName?: string;
}

export default function StatOutMoneyByMonth() {
  const [data, setData] = useState<MonthRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | undefined>(undefined);
  const [selectedLead, setSelectedLead] = useState<number | undefined>(undefined);
  const [leadOptions, setLeadOptions] = useState<{ value: number; label: string }[]>([]);

  // 从父页面 Crud 获取出款人列表（首次加载无筛选）
  const [allUserData, setAllUserData] = useState<MonthRow[]>([]);

  const loadData = useCallback(() => {
    setLoading(true);
    const params: any = {};
    if (selectedUser) params.outMoneyUserId = selectedUser;
    if (selectedLead) params.leadId = selectedLead;
    apiClient.request("/repaymentPlan/statOutMoneyByMonth", "GET", params).then((resp: any) => {
      const rows = resp.data || [];
      setData(rows);
      if (!selectedUser && !selectedLead) {
        setAllUserData(rows);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedUser, selectedLead]);

  useEffect(() => { loadData(); }, [loadData]);

  // 出款人选项：从首次加载的 children 中提取
  const userOptions = useMemo(() => {
    const map = new Map<number, string>();
    allUserData.forEach(row => {
      row.children?.forEach(child => {
        if (child.outMoneyUserId && child.outMoneyUserName) {
          map.set(child.outMoneyUserId, child.outMoneyUserName);
        }
      });
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [allUserData]);

  // 客户选项：从现有的出款人统计API获取
  useEffect(() => {
    if (!selectedUser) { setLeadOptions([]); return; }
    apiClient.request("/repaymentPlan/statPaidPlanByOutMoneyUserId", "GET", { page: 1, size: 500 }).then((resp: any) => {
      const rows = resp.data?.records || [];
      const map = new Map<number, string>();
      rows.forEach((row: any) => {
        row.children?.forEach((child: any) => {
          if (child.leadId && child.leadName) {
            map.set(child.leadId, child.leadName);
          }
        });
      });
      setLeadOptions(Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name })));
    });
  }, [selectedUser]);

  // 趋势图数据：只取有数据的月份，计划出款 vs 实际出款
  const chartData = useMemo(() => {
    return data.map(row => {
      const planTotal = (Number(row.planHelpAmount) || 0) + (Number(row.planBorrowAmount) || 0);
      const actualTotal = Number(row.outMoneyAmount) || 0;
      return {
        name: row.yearMonth || '',
        计划出款: planTotal,
        实际出款: actualTotal,
      };
    });
  }, [data]);

  // 日历数据：按年分组，只展示有数据的年
  const calendarByYear = useMemo(() => {
    const yearMap = new Map<number, MonthRow[]>();
    data.forEach(row => {
      if (!row.yearMonth) return;
      const y = parseInt(row.yearMonth.substring(0, 4));
      if (!yearMap.has(y)) yearMap.set(y, []);
      yearMap.get(y)!.push(row);
    });
    return Array.from(yearMap.entries()).sort((a, b) => b[0] - a[0]);
  }, [data]);

  const formatYAxis = (value: number) => {
    if (value >= 10000) return (value / 10000).toFixed(0) + '万';
    return String(value);
  };

  const getPlanTotal = (r: MonthRow) => (Number(r.planHelpAmount) || 0) + (Number(r.planBorrowAmount) || 0);

  const columns: any[] = [
    {
      title: "", dataIndex: "yearMonth", width: 100,
      render: (v: string, r: MonthRow) => (
        <span style={{ fontWeight: r.leadId ? 400 : (r.outMoneyUserId ? 500 : 700) }}>
          {r.leadId ? r.leadName : (r.outMoneyUserId ? r.outMoneyUserName : v)}
        </span>
      ),
    },
    {
      title: "计划出款", width: 100,
      render: (_: any, r: MonthRow) => {
        return <Text style={{ color: '#1677ff', fontWeight: 600 }}>{formatAmountZhCn(getPlanTotal(r))}</Text>;
      },
    },
    { title: "实际出款", dataIndex: "outMoneyAmount", width: 100, render: (v: number,row:any) => <Text style={{ fontWeight: 600 }}>{formatAmountZhCn(v+row.postingHelpAmount)}</Text> },
    { title: "实际出款(不含过账)", dataIndex: "outMoneyAmount", width: 100, render: (v: number,row:any) => <Text style={{ fontWeight: 600 }}>{formatAmountZhCn(v)}</Text> },

    {
      title: "差额", width: 90,
      render: (_: any, r: MonthRow) => {
        const diff = getPlanTotal(r) - (Number(r.outMoneyAmount) || 0);
        return <Text style={{ color: diff > 0 ? '#e53935' : (diff < 0 ? '#00b578' : '#999'), fontWeight: 600 }}>{formatAmountZhCn(diff)}</Text>;
      },
    },
    { title: "总代还", dataIndex: "creditHelpRepaymentPlanAmount", width: 90, render: (v: number,row:any) => formatAmountZhCn(row.loanHelpRepaymentPlanAmount+row.cardHelpRepaymentPlanAmount+row.postingHelpAmount) },
    { title: "贷款代还", dataIndex: "loanHelpRepaymentPlanAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "信用卡代还", dataIndex: "cardHelpRepaymentPlanAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "过账代还", dataIndex: "postingHelpAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "借出金额", dataIndex: "borrowAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "借出归还", dataIndex: "borrowReturnAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "利息", dataIndex: "interestAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
    { title: "佣金", dataIndex: "commissionAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
  ];

  // 汇总行：从月维度数据累加
  const summaryRow = useMemo(() => {
    const s = { planAmount: 0, outMoneyAmount: 0, diff: 0, creditHelp: 0, loanHelp: 0, cardHelp: 0, postingHelp: 0, borrowAmount: 0, borrowReturn: 0, interest: 0, commission: 0 };
    data.forEach(r => {
      const plan = getPlanTotal(r);
      const actual = Number(r.outMoneyAmount) || 0;
      s.planAmount += plan;
      s.outMoneyAmount += actual;
      s.diff += plan - actual;
      s.creditHelp += Number(r.creditHelpRepaymentPlanAmount) || 0;
      s.loanHelp += Number(r.loanHelpRepaymentPlanAmount) || 0;
      s.cardHelp += Number(r.cardHelpRepaymentPlanAmount) || 0;
      s.postingHelp += Number(r.postingHelpAmount) || 0;
      s.borrowAmount += Number(r.borrowAmount) || 0;
      s.borrowReturn += Number(r.borrowReturnAmount) || 0;
      s.interest += Number(r.interestAmount) || 0;
      s.commission += Number(r.commissionAmount) || 0;
    });
    return s;
  }, [data]);

  return (
    <Spin spinning={loading}>
      {/* 筛选栏 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Text style={{ fontSize: 13, color: '#666', whiteSpace: 'nowrap' }}>出款人</Text>
          <Select
            style={{ width: 160 }} placeholder="全部出款人" showClear
            value={selectedUser} onChange={(v: number) => { setSelectedUser(v); setSelectedLead(undefined); }}
            optionList={userOptions}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Text style={{ fontSize: 13, color: '#666', whiteSpace: 'nowrap' }}>客户</Text>
          <Select
            style={{ width: 160 }} placeholder={selectedUser ? "全部客户" : "请先选择出款人"} showClear
            disabled={!selectedUser}
            value={selectedLead} onChange={(v: number) => setSelectedLead(v)}
            optionList={leadOptions}
            filter
          />
        </div>
      </div>

      {/* 汇总栏 */}
      {data.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, padding: '10px 16px', marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div><Text style={{ fontSize: 12, color: '#999' }}>计划出款</Text><div><Text style={{ color: '#1677ff', fontWeight: 700, fontSize: 15 }}>{formatAmountZhCn(summaryRow.planAmount)}</Text></div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>实际出款</Text><div><Text style={{ fontWeight: 700, fontSize: 15 }}>{formatAmountZhCn(summaryRow.outMoneyAmount+summaryRow.postingHelp)}</Text></div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>差额</Text><div><Text style={{ color: summaryRow.diff > 0 ? '#e53935' : (summaryRow.diff < 0 ? '#00b578' : '#999'), fontWeight: 700, fontSize: 15 }}>{formatAmountZhCn(summaryRow.diff)}</Text></div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>实际出款(不含过账)</Text><div><Text style={{ fontWeight: 700, fontSize: 15 }}>{formatAmountZhCn(summaryRow.outMoneyAmount)}</Text></div></div>
          <div style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 16 }}><Text style={{ fontSize: 12, color: '#999' }}>总代还</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.loanHelp+summaryRow.cardHelp+summaryRow.postingHelp)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>贷款代还</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.loanHelp)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>信用卡代还</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.cardHelp)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>过账代还</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.postingHelp)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>借出金额</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.borrowAmount)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>借出归还</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.borrowReturn)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>利息</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.interest)}</div></div>
          <div><Text style={{ fontSize: 12, color: '#999' }}>佣金</Text><div style={{ fontWeight: 600, fontSize: 13 }}>{formatAmountZhCn(summaryRow.commission)}</div></div>
        </div>
      )}

      {/* 趋势图：计划出款 vs 实际出款 */}
      {chartData.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Text style={{ fontSize: 14, fontWeight: 600 }}>月度出款趋势</Text>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barGap={4} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={40} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} width={55} />
              <Tooltip formatter={(value: number, name: string) => [formatAmountZhCn(value), name]} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="计划出款" fill="#1677ff" radius={[3, 3, 0, 0]} barSize={24} />
              <Bar dataKey="实际出款" fill="#00b578" radius={[3, 3, 0, 0]} barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 年月日历：按年分组，只展示有数据的年 */}
      {calendarByYear.map(([year, months]) => (
        <div key={year} style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Text style={{ fontSize: 14, fontWeight: 600 }}>{year}年出款概览</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 8 }}>
            {months.map(item => {
              const plan = (Number(item.planHelpAmount) || 0) + (Number(item.planBorrowAmount) || 0);
              const actual = Number(item.outMoneyAmount) || 0;
              const m = parseInt(item.yearMonth!.substring(5));
              const isCurrentMonth = item.yearMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
              return (
                <div key={item.yearMonth} style={{
                  background: actual > 0 ? '#e6f7ff' : '#f6ffed',
                  border: isCurrentMonth ? '2px solid #1677ff' : '1px solid #f0f0f0',
                  borderRadius: 6, padding: '8px 10px',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                    {m}月
                    {isCurrentMonth && <span style={{ fontSize: 10, marginLeft: 4, color: '#1677ff' }}>当月</span>}
                  </div>
                  {plan > 0 && (
                    <div style={{ fontSize: 11, color: '#1677ff', fontWeight: 600, marginTop: 2 }}>计划 {formatAmountZhCn(plan)}</div>
                  )}
                  {actual > 0 && (
                    <div style={{ fontSize: 11, color: '#00b578', marginTop: 1 }}>实际 {formatAmountZhCn(actual)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        size="small"
        pagination={false}
        expandedRowRender={(record: MonthRow) => {
          if (!record.childRows || record.childRows.length === 0) return null;
          return (
            <div style={{ paddingLeft: 24, borderLeft: '3px solid #1677ff', marginLeft: 10 }}>
              <Table columns={columns} dataSource={record.childRows} rowKey="id" size="small" pagination={false} />
            </div>
          );
        }}
      />
    </Spin>
  );
}
